/**
 * Revision 7 (R7-C): the WorkWise cost estimate from POST /workwise/cost-estimate/,
 * shown in the step-3 Summary and on Review. The lines are grouped per server
 * (compute, SQL licence, OS disk, data disks, public IP, shared share) with a
 * subtotal; an unpriced line shows "—" and the backend's note. Before the first
 * answer a loading line is shown; when the endpoint fails the totals read
 * "Price unavailable" and nothing is blocked. The source goes in small text.
 * Only NewDeploymentPage.module.css classes.
 */
import { RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import styles from '@/pages/NewDeploymentPage.module.css'
import {
  type CostEstimateState,
  PRICE_UNAVAILABLE,
  costNotesText,
  formatCostAmount,
  groupCostLines,
  lineAmountText,
} from '@/utils/workwise/costEstimate'

interface CostEstimateProps {
  state: CostEstimateState
  /** Prefix of the test ids: `${testId}`, `${testId}-<role>`, `${testId}-line`, `${testId}-source`. */
  testId: string
}

function PricingNote({ text }: { text: string }) {
  return (
    <span className={styles.pricingNote} style={{ marginLeft: 0 }}>
      <RefreshCw size={11} className={styles.spin} /> {text}
    </span>
  )
}

/** The estimate's lines per server, with a loading or "Price unavailable" line when there is no answer. */
export function CostEstimateLines({ state, testId }: CostEstimateProps) {
  const groups = groupCostLines(state.estimate?.lines)
  return (
    <div className={styles.reviewTable} data-testid={testId} data-state={state.status} aria-busy={state.updating || undefined}>
      {state.status === 'loading' && <PricingNote text="Fetching Azure retail prices…" />}
      {state.status === 'ready' && state.updating && <PricingNote text="Updating prices…" />}
      {state.status === 'error' && (
        <span className={styles.fieldHint} style={{ marginTop: 0 }} data-testid={`${testId}-unavailable`}>
          {PRICE_UNAVAILABLE}: the estimate could not be calculated right now. This does not block the deployment.
        </span>
      )}
      {groups.map((group) => (
        <div key={group.role} className={styles.reviewTable} data-testid={`${testId}-${group.role}`}>
          <div className={styles.reviewRow}>
            <span className={styles.label}>{group.title}</span>
            <span className={styles.reviewValue} data-testid={`${testId}-${group.role}-subtotal`}>
              {formatCostAmount(group.subtotal, group.priced, group.unpriced)}
            </span>
          </div>
          {group.lines.map((line, index) => (
            <div
              key={`${line.item}-${index}`}
              className={styles.reviewRow}
              style={{ paddingLeft: 12 }}
              data-testid={`${testId}-line`}
              data-role={line.role}
              data-item={line.item}
            >
              <span className={styles.reviewLabel} style={{ flexShrink: 1 }}>
                {line.description}
                {line.note && (
                  <span className={styles.fieldHint} style={{ display: 'block', marginTop: 2 }}>
                    {line.note}
                  </span>
                )}
              </span>
              <span className={clsx(styles.reviewValue, styles.reviewValueMono)}>{lineAmountText(line)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** The backend's notes and the price source, in small text. */
export function CostEstimateSource({ state, testId }: CostEstimateProps) {
  if (!state.estimate) return null
  return (
    <div className={styles.fieldHint} data-testid={`${testId}-source`}>
      {costNotesText(state.estimate)}
    </div>
  )
}
